import { BRAND } from '@onuw/roles-data'
import { useEffect, useRef } from 'react'
import { AD_UNITS_ENABLED } from '../../lib/config'
import { cx } from '../../ui/Card'
import styles from './ads.module.css'

export interface AdSlotProps {
  /** AdSense ad unit id; without one nothing renders (so a missing setting can never break layout). */
  slot: string | undefined
  format?: 'auto' | 'horizontal' | 'rectangle'
  /** Reserved height so the ad never shifts content (CLS 0). */
  minHeight?: number
  className?: string
}

/**
 * One manual AdSense unit in a box that reserves its height. Only used on content pages, in the lobby while
 * waiting, and on the results screen; never at night, during the vote, or over the board. Renders nothing while
 * the units are switched off (`AD_UNITS_ENABLED`).
 */
export function AdSlot({ slot, format = 'auto', minHeight = 120, className }: AdSlotProps) {
  const ref = useRef<HTMLModElement>(null)
  useEffect(() => {
    if (!AD_UNITS_ENABLED || !slot || !ref.current) return
    if (ref.current.getAttribute('data-adsbygoogle-status')) return
    try {
      if (!window.adsbygoogle) window.adsbygoogle = []
      window.adsbygoogle.push({})
    } catch {
      // blocked or not loaded yet; the box stays empty
    }
  }, [slot])
  if (!AD_UNITS_ENABLED || !slot) return null
  return (
    <aside className={cx(styles.slot, className)} style={{ minHeight }} aria-label="Advertisement">
      <ins
        ref={ref}
        className="adsbygoogle"
        style={{ display: 'block' }}
        data-ad-client={`ca-${BRAND.adsensePublisherId}`}
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive="true"
      />
    </aside>
  )
}
